/**
 * Slice 32b — extras for this room’s inventory only, while still standing in it.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { useInventoryItems } from "@/hooks/useInventory";
import { useInventoryRooms } from "@/hooks/useInventoryRooms";
import {
  buildGuestGuideQueue,
  decidedGuestGuideCards,
  useSaveGuestGuide,
  type GuestGuideCard,
} from "@/hooks/useGuestGuide";
import { guestSentence, isLinenName } from "@/lib/guestGuideCopy";
import { uploadWalkthroughStill } from "@/lib/analyzeWalkthrough";
import { cn } from "@/lib/utils";

export function GuestGuideFlow({
  open,
  propertyId,
  roomName,
  nextRoomName,
  onClose,
  onGoNext,
  onAddItem,
}: {
  open: boolean;
  propertyId: string;
  roomName: string;
  nextRoomName?: string | null;
  onClose: () => void;
  onGoNext?: () => void;
  onAddItem?: () => void;
}) {
  if (!open || !roomName) return null;
  return createPortal(
    <GuestGuideInner
      propertyId={propertyId}
      roomName={roomName}
      nextRoomName={nextRoomName ?? null}
      onClose={onClose}
      onGoNext={onGoNext}
      onAddItem={onAddItem}
    />,
    document.body,
  );
}

function recapLine(card: GuestGuideCard): string {
  if (card.guest_guide_status === "no") return "Cleaners still track this. Guests won’t see it.";
  if (card.guest_copy?.trim()) return card.guest_copy.trim();
  return guestSentence({
    kind: card.kind,
    name: card.name,
    qty: card.guest_set_qty,
    place: card.guest_place || "",
  });
}

function GuestGuideInner({
  propertyId,
  roomName,
  nextRoomName,
  onClose,
  onGoNext,
  onAddItem,
}: {
  propertyId: string;
  roomName: string;
  nextRoomName: string | null;
  onClose: () => void;
  onGoNext?: () => void;
  onAddItem?: () => void;
}) {
  const { data: inv = [] } = useInventoryItems(propertyId);
  const { data: rooms = [] } = useInventoryRooms(propertyId);
  const save = useSaveGuestGuide();
  const filter = useMemo(() => ({ roomName }), [roomName]);
  const queue = useMemo(
    () => buildGuestGuideQueue(inv as Record<string, unknown>[], rooms, filter),
    [inv, rooms, filter],
  );
  const decided = useMemo(
    () => decidedGuestGuideCards(inv as Record<string, unknown>[], rooms, filter),
    [inv, rooms, filter],
  );
  const [index, setIndex] = useState(0);
  const [doneCount, setDoneCount] = useState(0);
  const [editingCard, setEditingCard] = useState<GuestGuideCard | null>(null);
  const originalTotal = Math.max(queue.length + doneCount, 1);
  const card = queue[Math.min(index, Math.max(0, queue.length - 1))] ?? null;
  const remaining = queue.length;
  const progressNow = remaining === 0 ? originalTotal : Math.min(doneCount + 1, originalTotal);

  const runSave = async (input: {
    propertyId: string;
    card: GuestGuideCard;
    visible: boolean;
    qty: number | null;
    place: string;
    placePhotoUrl: string | null;
  }) => {
    try {
      await save.mutateAsync(input);
    } catch (e) {
      toast({
        title: "Couldn’t save",
        description: e instanceof Error ? e.message : undefined,
        variant: "destructive",
      });
      throw e;
    }
  };

  if (editingCard) {
    return (
      <GuideCard
        propertyId={propertyId}
        card={editingCard}
        index={0}
        remaining={1}
        progressNow={1}
        progressTotal={1}
        busy={save.isPending}
        mode="edit"
        onClose={() => setEditingCard(null)}
        onBack={() => setEditingCard(null)}
        onSaved={() => setEditingCard(null)}
        save={runSave}
      />
    );
  }

  if (queue.length === 0) {
    const yesCount = decided.filter((c) => c.guest_guide_status === "yes").length;
    const noCount = decided.filter((c) => c.guest_guide_status === "no").length;
    const summary =
      decided.length === 0
        ? "Nothing left in this room."
        : `${decided.length} thing${decided.length === 1 ? "" : "s"} decided in this room.${
            yesCount && noCount
              ? ` ${yesCount} guests will hear about, ${noCount} ${noCount === 1 ? "stays" : "stay"} internal.`
              : yesCount
                ? " Guests will hear about these."
                : " Cleaners still track these. Guests won’t see them."
          }`;

    return (
      <div className="fixed inset-0 z-[80] flex flex-col bg-[#f4f7f9]" data-ptr-ignore>
        <div className="flex items-center justify-between bg-white px-5 pb-3 pt-5">
          <div className="text-[13px] font-bold tracking-wide text-[#64748b]">
            {Math.max(decided.length, originalTotal)} / {Math.max(decided.length, originalTotal)} · {roomName}
          </div>
          <button type="button" onClick={onClose} className="text-[15px] font-medium text-[#64748b]">
            Close
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 pb-8 pt-5">
          <div className="text-[24px] font-bold tracking-[-0.4px] text-[#0f1720]">{roomName} extras done</div>
          <p className="mt-2 text-[14px] font-medium leading-snug text-[#64748b]">{summary}</p>
          {decided.length > 0 ? (
            <div className="mt-4 overflow-hidden rounded-[18px] border border-[#e6edf1] bg-white">
              {decided.map((item, i) => (
                <div
                  key={item.id}
                  className={`px-4 py-3.5 ${i > 0 ? "border-t border-[#f1f5f7]" : ""}`}
                >
                  <div className="text-[12.5px] font-bold tracking-wide text-[#8494a2]">{item.name}</div>
                  <div className="mt-0.5 text-[14.5px] font-medium leading-snug text-[#0f1720]">{recapLine(item)}</div>
                  {item.guest_guide_status === "yes" ? (
                    <div className="mt-2 flex gap-3">
                      <button
                        type="button"
                        disabled={save.isPending}
                        onClick={() => setEditingCard(item)}
                        className="text-[13.5px] font-bold text-[#15776d] disabled:opacity-40"
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        disabled={save.isPending}
                        onClick={() => {
                          void runSave({
                            propertyId,
                            card: item,
                            visible: false,
                            qty: null,
                            place: "",
                            placePhotoUrl: null,
                          });
                        }}
                        className="text-[13.5px] font-bold text-[#b45353] disabled:opacity-40"
                      >
                        Remove from guests
                      </button>
                    </div>
                  ) : (
                    <div className="mt-2">
                      <button
                        type="button"
                        disabled={save.isPending}
                        onClick={() => setEditingCard({ ...item, guest_guide_status: "pending" })}
                        className="text-[13.5px] font-bold text-[#15776d] disabled:opacity-40"
                      >
                        Add for guests
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : null}
        </div>
        <div className="border-t border-[#e6edf1] bg-white px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3">
          {onAddItem ? (
            <button
              type="button"
              onClick={onAddItem}
              className="mb-2.5 flex h-14 w-full items-center justify-center rounded-[14px] border-[1.5px] border-[#e6edf1] bg-white text-[16.5px] font-bold text-[#0f1720]"
            >
              Add another item
            </button>
          ) : null}
          {nextRoomName ? (
            <>
              <button
                type="button"
                onClick={() => (onGoNext ? onGoNext() : onClose())}
                className="flex h-14 w-full items-center justify-center rounded-[14px] bg-[#24a89a] text-[16.5px] font-bold text-white"
              >
                Go to {nextRoomName}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="mt-2 flex h-11 w-full items-center justify-center text-[14.5px] font-bold text-[#64748b]"
              >
                Back to rooms
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="flex h-14 w-full items-center justify-center rounded-[14px] bg-[#24a89a] text-[16.5px] font-bold text-white"
            >
              Onboarding complete
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <GuideCard
      propertyId={propertyId}
      card={card!}
      index={index}
      remaining={remaining}
      progressNow={progressNow}
      progressTotal={originalTotal}
      busy={save.isPending}
      mode="create"
      onClose={onClose}
      onBack={() => setIndex((i) => Math.max(0, i - 1))}
      onSaved={() => {
        setDoneCount((n) => n + 1);
      }}
      save={runSave}
    />
  );
}

function GuideCard({
  propertyId,
  card,
  index,
  remaining,
  progressNow,
  progressTotal,
  busy,
  mode,
  onClose,
  onBack,
  onSaved,
  save,
}: {
  propertyId: string;
  card: GuestGuideCard;
  index: number;
  remaining: number;
  progressNow: number;
  progressTotal: number;
  busy: boolean;
  mode: "create" | "edit";
  onClose: () => void;
  onBack: () => void;
  onSaved: () => void;
  save: (input: {
    propertyId: string;
    card: GuestGuideCard;
    visible: boolean;
    qty: number | null;
    place: string;
    placePhotoUrl: string | null;
  }) => Promise<void>;
}) {
  const linen = isLinenName(card.name);
  const [yes, setYes] = useState(true);
  const [place, setPlace] = useState("");
  const [photoUrl, setPhotoUrl] = useState<string | null>(null);
  const [photoLocal, setPhotoLocal] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const editingExisting = mode === "edit" || card.guest_guide_status === "yes";
    setYes(card.guest_guide_status !== "no");
    setPlace(editingExisting ? card.guest_place || "" : "");
    setPhotoUrl(editingExisting ? card.guest_place_photo_url : null);
    setPhotoLocal(editingExisting ? card.guest_place_photo_url : null);
  }, [
    card.id,
    card.name,
    card.guest_guide_status,
    card.guest_place,
    card.guest_place_photo_url,
    mode,
  ]);

  const sentence = yes
    ? guestSentence({
        kind: card.kind,
        name: card.name,
        qty: null,
        place,
      })
    : "No extras needed for this item.";
  const placeMissing = yes && !place.trim();
  const photoMissing = yes && !photoUrl;
  const blocked = placeMissing || photoMissing;
  const lastThingPhoto = photoMissing && !placeMissing;

  const onFile = async (file: File | null) => {
    if (!file) return;
    setUploading(true);
    try {
      const local = URL.createObjectURL(file);
      setPhotoLocal(local);
      const url = await uploadWalkthroughStill(file, propertyId);
      setPhotoUrl(url);
    } catch (e) {
      toast({
        title: "Couldn’t save photo",
        description: e instanceof Error ? e.message : undefined,
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const next = async () => {
    if (yes && blocked) return;
    await save({
      propertyId,
      card: { ...card, guest_place_photo_url: photoUrl },
      visible: yes,
      qty: null,
      place: place.trim(),
      placePhotoUrl: photoUrl,
    });
    onSaved();
  };

  return (
    <div className="fixed inset-0 z-[80] flex flex-col bg-[#f4f7f9]" data-ptr-ignore>
      <div className="flex items-center justify-between bg-white px-5 pb-3 pt-5">
        <div className="text-[13px] font-bold tracking-wide text-[#64748b]">
          {mode === "edit"
            ? `Edit · ${card.room || "Room"}`
            : `${progressNow} / ${progressTotal} · ${card.room || "Room"}`}
        </div>
        <button type="button" onClick={onClose} className="text-[15px] font-medium text-[#64748b]">
          Close
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-5 pb-32 pt-4">
        <div className="flex gap-3">
          <div className="h-[84px] w-[84px] shrink-0 overflow-hidden rounded-[14px] bg-[#e6edf1]">
            {card.photoUrl ? <img src={card.photoUrl} alt="" className="h-full w-full object-cover" /> : null}
          </div>
          <div>
            <div className="text-[18px] font-bold text-[#0f1720]">{card.name}</div>
            <div className="mt-1 text-[13px] font-medium text-[#8494a2]">Inventory · from your walk</div>
          </div>
        </div>

        <p className="mt-5 text-[16px] font-bold leading-snug text-[#0f1720]">
          {sentence}
          {placeMissing ? <span className="font-bold text-[#94a3b8]">…</span> : null}
        </p>

        <div className="mt-5 text-[12px] font-bold tracking-wide text-[#64748b]">EXTRAS FOR GUESTS</div>
        <div className="mt-2 flex gap-2">
          <button
            type="button"
            onClick={() => setYes(true)}
            className={cn(
              "flex min-h-12 flex-1 items-center justify-center rounded-[12px] border-[1.5px] px-2 text-center text-[15px] font-bold leading-tight",
              yes ? "border-[#24a89a] bg-[#e4f2f0] text-[#0d453f]" : "border-[#e6edf1] text-[#64748b]",
            )}
          >
            Yes, leave extras
          </button>
          <button
            type="button"
            onClick={() => setYes(false)}
            className={cn(
              "flex min-h-12 flex-1 items-center justify-center rounded-[12px] border-[1.5px] px-2 text-center text-[15px] font-bold leading-tight",
              !yes ? "border-[#24a89a] bg-[#e4f2f0] text-[#0d453f]" : "border-[#e6edf1] text-[#64748b]",
            )}
          >
            No extras needed
          </button>
        </div>

        {yes ? (
          <>
            <div className="mt-4">
              <div className="text-[12px] font-bold tracking-wide text-[#64748b]">EXTRAS ARE IN</div>
              <input
                value={place}
                onChange={(e) => setPlace(e.currentTarget.value)}
                placeholder="the basket"
                className="mt-2 h-12 w-full rounded-[12px] border-[1.5px] border-[#e6edf1] bg-white px-3 text-[16px] outline-none"
              />
            </div>
            <div className="mt-4">
              <div className="flex items-baseline justify-between gap-2">
                <div className="text-[12px] font-bold tracking-wide text-[#64748b]">PHOTO OF WHERE EXTRAS ARE</div>
                {lastThingPhoto ? (
                  <div className="text-[12.5px] font-bold text-[#15776d]">Last thing left</div>
                ) : null}
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => void onFile(e.target.files?.[0] ?? null)}
              />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className={cn(
                  "mt-2 flex h-[140px] w-full flex-col items-center justify-center overflow-hidden rounded-[16px] border-[1.5px] border-dashed bg-white",
                  lastThingPhoto ? "border-[#24a89a]" : "border-[#cfe6e2]",
                )}
              >
                {photoLocal ? (
                  <img src={photoLocal} alt="" className="h-full w-full object-cover" />
                ) : (
                  <>
                    <div className="text-[15px] font-bold text-[#0d453f]">
                      {linen ? "Photo of the closet / spare set" : "Photo of the drawer / closet / hook"}
                    </div>
                    <div className="mt-1 text-[13px] font-medium text-[#64748b]">
                      {uploading
                        ? "Saving…"
                        : linen
                          ? "The bed photo is what they are. This is where the spare set lives."
                          : "Not the product — the place they look"}
                    </div>
                  </>
                )}
              </button>
              {photoLocal ? (
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="mt-2 text-[14px] font-bold text-[#15776d]"
                >
                  Retake
                </button>
              ) : null}
            </div>
          </>
        ) : (
          <p className="mt-5 text-[14px] font-medium text-[#64748b]">
            Guests will not be told about this. Tap the green button to skip it.
          </p>
        )}
      </div>
      <div className="border-t border-[#e6edf1] bg-white px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3">
        {blocked ? (
          <p className="mb-2 text-center text-[13px] font-medium text-[#8494a2]">
            Add where it is and a photo of that place.
          </p>
        ) : null}
        <div className="flex gap-2.5">
          <button
            type="button"
            onClick={mode === "edit" || index === 0 ? onClose : onBack}
            className="flex h-14 flex-1 items-center justify-center rounded-[14px] border-[1.5px] border-[#e6edf1] text-[16px] font-bold"
          >
            Back
          </button>
          <button
            type="button"
            disabled={Boolean(blocked) || busy}
            onClick={() => void next()}
            className="flex h-14 flex-1 items-center justify-center rounded-[14px] bg-[#24a89a] text-[16px] font-bold text-white disabled:opacity-40"
          >
            {busy ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : mode === "edit" ? (
              "Save"
            ) : remaining <= 1 ? (
              "Done"
            ) : (
              "Next"
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
